import { isEvenSampling } from 'components/attacks/utils/display-targets'
import { keysToSnakeCase } from 'utils/obj'

import type { CamelCasedProperties, SnakeCasedPropertiesDeep } from 'type-fest'
import type { AttackCliArgs } from 'types/api/attacks/details/targeting'
import type { InfraAttackNode } from 'types/api/scenarios/scenario'

/**
 * This block of comments was previously living in `AttackGraphNode`.
 * These functions are used as a temporary measure while we transition Scenario creation to be based off of
 * the attackConfiguration instead of targetDefinition and impactDefinition. Due to the present complexity present in
 * the Scenario create/edit workflow, we still need to be able to fall back to the previous configurations.
 */
export function getCliArgs(node: InfraAttackNode): AttackCliArgs {
  const thisNode = keysToSnakeCase(node)
  if (thisNode.attack_configuration) {
    return thisNode.attack_configuration.command.infra_command_args?.cli_args
  }

  return thisNode.impact_definition.infra_command_args.cli_args
}

export function getInfraCommandType(node) {
  const thisNode = keysToSnakeCase(node)
  if (thisNode.attack_configuration) {
    return thisNode.attack_configuration.command.infra_command_type
  }

  return thisNode.impact_definition.infra_command_type
}

export function getImpactDefinition(node) {
  const thisNode = keysToSnakeCase(node)
  if (thisNode.attack_configuration) {
    return thisNode.attack_configuration.command.infra_command_args
  }

  return thisNode.impact_definition.infra_command_args
}

export function getTargetType(
  node: InfraAttackNode | CamelCasedProperties<InfraAttackNode>
) {
  const thisNode = keysToSnakeCase(
    node
  ) as SnakeCasedPropertiesDeep<InfraAttackNode>

  const targetingStrategy =
    thisNode.attack_configuration?.targets?.[0]?.targeting_strategy

  if (targetingStrategy) {
    return targetingStrategy.type
  }

  return thisNode?.target_definition?.target_type ?? 'Host'
}

export function getBlastRadiusValue(node: InfraAttackNode) {
  const thisNode = keysToSnakeCase(node)

  if (!thisNode) return

  if (thisNode.attack_configuration) {
    const sampling = thisNode.attack_configuration.sampling
    const samplingPercent =
      sampling && isEvenSampling(sampling) ? sampling.percent : undefined

    if (samplingPercent !== undefined) {
      return `${samplingPercent}%`
    }

    return thisNode.attack_configuration.sampling?.count
  }

  const targetDefinitionStrategy = thisNode.target_definition?.strategy

  if (!targetDefinitionStrategy) return

  return targetDefinitionStrategy.percentage
    ? `${targetDefinitionStrategy.percentage}%`
    : targetDefinitionStrategy.count
}

/**
 * End of temporary transition functions
 */
